
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Edit3, Save, X } from "lucide-react";

interface ExtendedUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone?: string;
  linkedin_url?: string;
  location_city?: string;
  location_state?: string;
  location_country?: string;
  timezone?: string;
  bio?: string;
  years_experience?: number;
  specializations?: string[];
  work_hours_start?: string;
  work_hours_end?: string;
  preferred_communication?: string;
}

interface PersonalInfoSectionProps {
  userProfile: ExtendedUserProfile;
  onUpdate: (updates: Partial<ExtendedUserProfile>) => Promise<void>;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  userProfile,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: userProfile.first_name || "",
    last_name: userProfile.last_name || "",
    bio: userProfile.bio || "",
  });

  useEffect(() => {
    setFormData({
      first_name: userProfile.first_name || "",
      last_name: userProfile.last_name || "",
      bio: userProfile.bio || "",
    });
  }, [userProfile]);

  const handleSave = async () => {
    const updates: Partial<ExtendedUserProfile> = {};
    
    if (formData.first_name !== (userProfile.first_name || "")) {
      updates.first_name = formData.first_name || undefined;
    }
    if (formData.last_name !== (userProfile.last_name || "")) {
      updates.last_name = formData.last_name || undefined;
    }
    if (formData.bio !== (userProfile.bio || "")) {
      updates.bio = formData.bio || undefined;
    }

    if (Object.keys(updates).length > 0) {
      await onUpdate(updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      first_name: userProfile.first_name || "",
      last_name: userProfile.last_name || "",
      bio: userProfile.bio || "",
    });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input 
              id="firstName" 
              value={formData.first_name} 
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="Enter your first name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input 
              id="lastName" 
              value={formData.last_name} 
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Enter your last name"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="bio">Bio</Label>
          <Textarea 
            id="bio" 
            value={formData.bio} 
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            placeholder="Tell us about yourself..."
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">
            {formData.bio.length}/500 characters
          </p>
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Personal Information</h3>
        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
          <Edit3 className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">First Name</Label>
          <p className="text-sm">{userProfile.first_name || "Not set"}</p>
        </div>
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">Last Name</Label>
          <p className="text-sm">{userProfile.last_name || "Not set"}</p>
        </div>
      </div>
      
      <div>
        <Label className="block text-muted-foreground text-sm mb-1">Email</Label>
        <p className="text-sm">{userProfile.email}</p>
        <p className="text-xs text-muted-foreground">Email cannot be changed</p>
      </div>
      
      <div>
        <Label className="block text-muted-foreground text-sm mb-1">Bio</Label>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {userProfile.bio || "Add a bio to tell others about yourself"}
        </p>
      </div>
    </div>
  );
};
